/**
 * Chat scroll intents. A search locate must win over "view original message",
 * which in turn wins over the unread line jump.
 */
export const MESSAGE_SCROLL_REASONS = {
    SEARCH: "search",
    ORIGINAL: "original",
    UNREAD: "unread",
};

const MESSAGE_SCROLL_PRIORITIES = {
    [MESSAGE_SCROLL_REASONS.SEARCH]: 3,
    [MESSAGE_SCROLL_REASONS.ORIGINAL]: 2,
    [MESSAGE_SCROLL_REASONS.UNREAD]: 1,
};

/** "View original" waits up to ~10s for its target to mount before giving up. */
export const MESSAGE_SCROLL_RESERVATION_TTL_MS = 12000;

/**
 * How long to keep pending_scroll_to_sid after a real unread jump scroll so
 * resize/scrollToBottom cannot yank the list to the bottom mid-settle.
 * Already-in-view jumps clear pending immediately (no delay).
 */
export const CHAT_MESSAGE_JUMP_SETTLE_MS = 300;

/**
 * How long to ignore viewport reads after clearPendingScroll for an unread jump.
 */
export const UNREAD_VISIBLE_READ_SETTLE_MS = 400;

/**
 * Fraction of the message *or* of the scrollport that must be on screen for a
 * viewport read. Tall messages use the scrollport cap so a 2000-word bubble can
 * still count as read when ~40% of the list shows it.
 */
export const UNREAD_VISIBLE_READ_RATIO = 0.4;

/**
 * Dense IntersectionObserver thresholds so tall messages keep firing while the
 * visible slice grows (a lone `0` / `0.4` never re-fires mid-scroll).
 */
export const UNREAD_VISIBLE_READ_THRESHOLDS = Array.from(
    {length: 101},
    (_, i) => i / 100,
);

/**
 * Whether enough of a message is on screen to count as read.
 * Uses min(ratio × message height, ratio × root height) so short messages keep
 * the old “~40% of bubble” rule and tall ones only need ~40% of the viewport.
 *
 * @param {{intersection_height?: number, element_height?: number, root_height?: number, ratio?: number}} options
 * @returns {boolean}
 */
export function isMessageVisibleEnoughForRead({
    intersection_height = 0,
    element_height = 0,
    root_height = 0,
    ratio = UNREAD_VISIBLE_READ_RATIO,
} = {}) {
    if (
        intersection_height <= 0 ||
        element_height <= 0 ||
        root_height <= 0 ||
        ratio <= 0
    ) {
        return false;
    }

    const needed = Math.min(element_height * ratio, root_height * ratio);

    return intersection_height + 0.5 >= needed;
}

export function getMessageScrollPriority(reason) {
    return MESSAGE_SCROLL_PRIORITIES[reason] ?? 0;
}

/**
 * @param {string|null} current_reason reason of the scroll already requested
 * @param {string|null} next_reason reason of the scroll being requested now
 * @returns {boolean}
 */
export function canReplaceMessageScroll(current_reason, next_reason) {
    return (
        getMessageScrollPriority(next_reason) >=
        getMessageScrollPriority(current_reason)
    );
}

/**
 * Viewport reads must wait out an unread jump: pending target is set, or the
 * post-clear settle window is still open.
 *
 * @param {{pending_scroll_to_sid?: string|null, suppress_until?: number, now?: number}} options
 * @returns {boolean}
 */
export function shouldSuppressVisibleRead({
    pending_scroll_to_sid = null,
    suppress_until = 0,
    now = Date.now(),
} = {}) {
    if (pending_scroll_to_sid) {
        return true;
    }

    return now < suppress_until;
}

/**
 * Re-seeding on an already-open chat rewinds last-read to the Mongo marker
 * and makes a second AQ click restart at the first unread.
 *
 * @param {boolean} was_open
 * @param {boolean} is_opened
 * @returns {boolean}
 */
export function shouldReseedUnreadOnConversationOpen(was_open, is_opened) {
    return Boolean(is_opened) && !was_open;
}

/**
 * Visible unread acknowledgement (settle → mark read / reconcile AQ) must only
 * run for the conversation the user is actually viewing on the message tab.
 * Background Twilio loads must not clear AQ via the shared chat scroller.
 *
 * @param {{
 *   is_current?: boolean,
 *   is_conversation_open?: boolean,
 *   is_message_tab?: boolean,
 * }} input
 * @returns {boolean}
 */
export function shouldRunVisibleUnreadAcknowledgement({
    is_current = false,
    is_conversation_open = false,
    is_message_tab = false,
} = {}) {
    return Boolean(is_current && is_conversation_open && is_message_tab);
}

/** How many times to wait on a pending unread jump before treating it as stuck. */
export const UNREAD_ACK_PENDING_SCROLL_MAX_RETRIES = 2;

/**
 * Visit / messages-fetched ack while a scroll jump is still pending.
 * Wait only while an unread jump can still finish. If the target is already
 * visible, or the retry budget is spent, clear that unread flag. Never clear
 * a search or view-original jump.
 *
 * @param {{
 *   pending_scroll_to_sid?: string|null,
 *   pending_scroll_reason?: string|null,
 *   unread_visible?: boolean,
 *   attempt?: number,
 *   max_retries?: number,
 * }} input
 * @returns {'proceed'|'wait'|'clear'|'stop'}
 */
export function resolvePendingScrollAcknowledgement({
    pending_scroll_to_sid = null,
    pending_scroll_reason = null,
    unread_visible = false,
    attempt = 0,
    max_retries = UNREAD_ACK_PENDING_SCROLL_MAX_RETRIES,
} = {}) {
    if (!pending_scroll_to_sid) {
        return "proceed";
    }

    const stuck = Boolean(unread_visible) || attempt >= max_retries;
    if (!stuck) {
        return "wait";
    }

    if (
        pending_scroll_reason &&
        pending_scroll_reason !== MESSAGE_SCROLL_REASONS.UNREAD
    ) {
        return "stop";
    }

    return "clear";
}

/**
 * How to persist the Mongo manual-unread marker after a local last-read
 * advance. Only when the viewer marked the conversation unread.
 * Real Twilio unread never writes this marker.
 * Follows Pinia, not Twilio catch-up (Twilio is never rewound).
 *
 * @param {{
 *   manual_message_index?: number|null,
 *   last_read_message_index?: number|null,
 *   latest_index?: number|null,
 *   held?: boolean,
 *   ignore_hold?: boolean,
 * }} input
 * @returns {{action: 'none'|'put'|'delete', message_index: number|null}}
 */
export function resolveManualUnreadPersistence({
    manual_message_index = null,
    last_read_message_index = null,
    latest_index = null,
    held = false,
    ignore_hold = false,
} = {}) {
    if (manual_message_index == null || (held && !ignore_hold)) {
        return {action: "none", message_index: null};
    }

    if (last_read_message_index == null) {
        return {action: "none", message_index: null};
    }

    if (latest_index != null && last_read_message_index >= latest_index) {
        return {action: "delete", message_index: null};
    }

    const next_message_index = last_read_message_index + 1;

    if (next_message_index <= manual_message_index) {
        return {action: "none", message_index: null};
    }

    return {action: "put", message_index: next_message_index};
}

/**
 * Keep the furthest manual-unread cursor when several reads persist at once.
 * An older read must not replace a newer one. The same cursor keeps
 * `ignore_hold` if either caller is an explicit Attention Queue step.
 *
 * @param {{local_last_read?: number|null, ignore_hold?: boolean}|null} current
 * @param {{local_last_read?: number|null, ignore_hold?: boolean}|null} next
 * @returns {{local_last_read: number|null, ignore_hold: boolean}|null}
 */
export function coalesceManualUnreadPersist(current, next) {
    if (!next) {
        return current ?? null;
    }

    if (!current) {
        return {
            local_last_read: next.local_last_read ?? null,
            ignore_hold: Boolean(next.ignore_hold),
        };
    }

    const next_read = next.local_last_read ?? null;
    const current_read = current.local_last_read ?? null;

    if (next_read == null) {
        return current;
    }

    if (current_read == null || next_read > current_read) {
        return {
            local_last_read: next_read,
            ignore_hold: Boolean(next.ignore_hold),
        };
    }

    if (next_read === current_read && next.ignore_hold) {
        return {
            local_last_read: current_read,
            ignore_hold: true,
        };
    }

    return current;
}

/**
 * A failed marker write may restore the previous index only while this write
 * is still the current marker. A newer cursor must win.
 *
 * @param {number|null} current_index
 * @param {number|null} written_index
 * @returns {boolean}
 */
export function shouldRevertManualUnreadMarker(current_index, written_index) {
    return current_index === written_index;
}

/**
 * Next Attention Queue conversation.
 * AQ > 1 always opens the next queued thread (FIFO, wrapping).
 * AQ = 1 stays on the open thread so the click can step remaining unreads.
 *
 * @param {{
 *   queued?: Array<{id?: number|string}>,
 *   current_open_id?: number|string|null,
 * }} input
 * @returns {{id?: number|string}|null}
 */
export function resolveUnreadQueueTarget({
    queued = [],
    current_open_id = null,
} = {}) {
    if (!Array.isArray(queued) || !queued.length) {
        return null;
    }

    const current_index = queued.findIndex(
        (conversation) => conversation?.id === current_open_id,
    );

    if (current_index < 0) {
        return queued[0];
    }

    if (queued.length === 1) {
        return queued[0];
    }

    return queued[(current_index + 1) % queued.length];
}

/**
 * Effective last-read when a per-user manual unread marker is active.
 * Manual `message_index` is the first unread message; last-read is one before it.
 *
 * @param {number|null|undefined} twilio_last_read
 * @param {number|null|undefined} manual_message_index
 * @returns {number|null}
 */
export function effectiveLastReadMessageIndex(
    twilio_last_read,
    manual_message_index,
) {
    if (manual_message_index == null) {
        return twilio_last_read ?? null;
    }

    const manual_last_read =
        manual_message_index <= 0 ? null : manual_message_index - 1;

    if (twilio_last_read == null) {
        return manual_last_read;
    }

    if (manual_last_read == null) {
        return null;
    }

    return Math.min(twilio_last_read, manual_last_read);
}

/**
 * How many messages are unread from a manual first-unread index through latest.
 *
 * @param {number|null|undefined} latest_index
 * @param {number} message_index
 * @returns {number}
 */
export function manualUnreadCountFromLatest(latest_index, message_index) {
    if (latest_index == null || message_index == null) {
        return 0;
    }

    return Math.max(0, latest_index - message_index + 1);
}

/**
 * How many messages remain after a last-read index through latest.
 *
 * @param {number|null|undefined} latest_index
 * @param {number|null|undefined} last_read_message_index
 * @returns {number}
 */
export function unreadCountAfterLastRead(latest_index, last_read_message_index) {
    if (latest_index == null) {
        return 0;
    }

    if (last_read_message_index == null) {
        return Math.max(0, latest_index + 1);
    }

    return Math.max(0, latest_index - last_read_message_index);
}

/**
 * Loaded messages still past the user's read horizon. Prefer this over
 * index subtraction when deciding AQ membership (indexes may have gaps).
 *
 * @param {Array<{index?: number}|null|undefined>|null|undefined} messages
 * @param {number|null|undefined} last_read_message_index
 * @returns {Array<{index?: number}>}
 */
export function messagesAfterLastRead(
    messages = [],
    last_read_message_index = null,
) {
    if (!Array.isArray(messages) || !messages.length) {
        return [];
    }

    return messages.filter((message) => {
        if (message?.index == null) {
            return false;
        }

        if (last_read_message_index == null) {
            return true;
        }

        return message.index > last_read_message_index;
    });
}

/**
 * @param {Array<{index?: number}|null|undefined>|null|undefined} messages
 * @param {number|null|undefined} last_read_message_index
 * @returns {number}
 */
export function countMessagesAfterLastRead(
    messages = [],
    last_read_message_index = null,
) {
    return messagesAfterLastRead(messages, last_read_message_index).length;
}

/**
 * Decide how to clear a sticky Activity Queue entry when visit/scroll cannot
 * find a user-facing unread row to mark.
 *
 * Returns the last-read index to sync with, or `null` when no reconcile.
 *
 * @param {{
 *   unread_messages_count?: number|null,
 *   last_read_message_index?: number|null,
 *   latest_index?: number|null,
 *   first_user_unread_index?: number|null,
 *   separator_after_index?: number|null,
 *   loaded_messages?: number,
 * }} input
 * @returns {number|null}
 */
export function resolveStaleActivityQueueUnreadReconcile({
    unread_messages_count = 0,
    last_read_message_index = null,
    latest_index = null,
    first_user_unread_index = null,
    separator_after_index = null,
    loaded_messages = 0,
} = {}) {
    if (!(unread_messages_count > 0)) {
        return null;
    }

    // Never-read horizon: do not invent a local last-read. That races the
    // Message.vue visible-read observer (which then no-ops as "already read")
    // and never writes Twilio — so AQ returns on refresh. Leave never-read
    // for IO / invite separator highlight to resolve.
    if (last_read_message_index == null) {
        return null;
    }

    const computed = unreadCountAfterLastRead(
        latest_index,
        last_read_message_index,
    );

    // Local last-read already covers the latest loaded message.
    if (computed === 0) {
        return last_read_message_index ?? latest_index;
    }

    // Separator is up, messages are loaded, but nothing non-own remains.
    if (
        first_user_unread_index == null &&
        separator_after_index != null &&
        loaded_messages > 0 &&
        latest_index != null
    ) {
        return latest_index;
    }

    // No separator / no first unread, but live count disagrees with last-read.
    if (
        first_user_unread_index == null &&
        separator_after_index == null &&
        loaded_messages > 0 &&
        unread_messages_count !== computed
    ) {
        return last_read_message_index;
    }

    return null;
}

/**
 * What a second Attention Queue click should do when the queued conversation
 * is already open. Uses the live last-read cursor, not the frozen separator.
 *
 * Manual unread consumes one message (Pinia only). Twilio unread consumes every
 * unread message currently in the viewport so short stacked rows all mark read.
 *
 * @param {{
 *   messages?: Array<{index?: number, sid?: string, is_author?: boolean}>,
 *   last_read_message_index?: number|null,
 *   visible_indexes?: number[],
 *   is_manual?: boolean,
 *   ignore_own_messages?: boolean,
 * }} input
 * @returns {{
 *   action: 'none'|'scroll'|'consume'|'consume-and-scroll',
 *   consume_index: number|null,
 *   scroll_to: {index?: number, sid?: string, is_author?: boolean}|null,
 * }}
 */
export function resolveActivityQueueUnreadStep({
    messages = [],
    last_read_message_index = null,
    visible_indexes = [],
    is_manual = false,
    ignore_own_messages = false,
} = {}) {
    const unread_options = {ignore_own_messages};
    const next = findFirstUnreadMessage(
        messages,
        last_read_message_index,
        unread_options,
    );

    if (!next) {
        return {action: "none", consume_index: null, scroll_to: null};
    }

    const visible = new Set(visible_indexes);
    if (!visible.has(next.index)) {
        return {action: "scroll", consume_index: null, scroll_to: next};
    }

    let consume_index = next.index;
    if (!is_manual) {
        for (const message of messages) {
            if (message?.index == null) {
                continue;
            }

            if (
                last_read_message_index != null &&
                message.index <= last_read_message_index
            ) {
                continue;
            }

            if (ignore_own_messages && message.is_author) {
                continue;
            }

            if (visible.has(message.index) && message.index > consume_index) {
                consume_index = message.index;
            }
        }
    }

    const remaining = findFirstUnreadMessage(
        messages,
        consume_index,
        unread_options,
    );

    if (!remaining) {
        return {action: "consume", consume_index, scroll_to: null};
    }

    return {
        action: "consume-and-scroll",
        consume_index,
        scroll_to: remaining,
    };
}

/**
 * First loaded message the current user has not read yet. Twilio indexes are
 * authoritative here; a `null` last read index means nothing was read at all.
 *
 * Twilio counts the user's own messages as unread until their read index
 * catches up, so `ignore_own_messages` is needed wherever the result is shown
 * to the user as something new to read.
 *
 * @param {Array<{index?: number, sid?: string, is_author?: boolean}>} messages
 * @param {number|null} last_read_message_index
 * @param {{ignore_own_messages?: boolean}} options
 * @returns {{index?: number, sid?: string}|null}
 */
export function findFirstUnreadMessage(
    messages,
    last_read_message_index,
    {ignore_own_messages = false} = {},
) {
    if (!Array.isArray(messages) || !messages.length) {
        return null;
    }

    return (
        messages.find(
            (message) =>
                message?.index != null &&
                (last_read_message_index == null ||
                    message.index > last_read_message_index) &&
                !(ignore_own_messages && message.is_author),
        ) ?? null
    );
}

/**
 * Whether an invite-added message lists the given user profile id in
 * `data.user_profiles` / `user_profiles` (objects or nested arrays).
 * Requires `type: "invite"` and `sub_type: "added"` (not "removed").
 *
 * @param {{type?: string, is_invite?: boolean, data?: {sub_type?: string, user_profiles?: unknown}, user_profiles?: unknown, sub_type?: string}|null|undefined} message
 * @param {number|string|null|undefined} current_user_profile_id
 * @returns {boolean}
 */
export function inviteMessageIncludesProfileId(message, current_user_profile_id) {
    if (current_user_profile_id == null) {
        return false;
    }

    if (message?.type !== "invite" && !message?.is_invite) {
        return false;
    }

    const sub_type = message?.data?.sub_type ?? message?.sub_type;
    if (sub_type !== "added") {
        return false;
    }

    const profiles =
        message?.data?.user_profiles ?? message?.user_profiles ?? null;

    if (!Array.isArray(profiles)) {
        return false;
    }

    const target = String(current_user_profile_id);

    return profiles.some((entry) => {
        const profile = Array.isArray(entry) ? entry[0] : entry;
        const id = profile?.id ?? profile?.user_profile_id;

        return id != null && String(id) === target;
    });
}

/**
 * For never-read visits (Twilio last-read null/0), place the New Messages line
 * just above the latest `invite` that includes the current user profile, so the
 * highlight range is that invite through the most recent message.
 *
 * Non-DM conversations with exactly two messages highlight everything (`-1`).
 *
 * @param {Array<{index?: number, type?: string, is_invite?: boolean, data?: object}>|null|undefined} messages
 * @param {number|string|null|undefined} current_user_profile_id
 * @param {{message_count?: number|null, is_dm?: boolean}} options
 * @returns {number|null} `separator_after_index`, or `null` when no matching
 *   invite exists. Returns `-1` when the invite is at index 0 or the two-message
 *   non-DM shortcut applies.
 */
export function findNeverReadInviteSeparatorAfterIndex(
    messages,
    current_user_profile_id,
    {message_count = null, is_dm = false} = {},
) {
    const total_count =
        message_count != null
            ? message_count
            : Array.isArray(messages)
              ? messages.length
              : 0;

    if (total_count === 2 && !is_dm) {
        return -1;
    }

    if (!Array.isArray(messages) || !messages.length) {
        return null;
    }

    if (current_user_profile_id == null) {
        return null;
    }

    const sorted = messages
        .filter((message) => message?.index != null)
        .slice()
        .sort((a, b) => a.index - b.index);

    if (!sorted.length) {
        return null;
    }

    let invite_index = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
        if (inviteMessageIncludesProfileId(sorted[i], current_user_profile_id)) {
            invite_index = sorted[i].index;
            break;
        }
    }

    if (invite_index == null) {
        return null;
    }

    return invite_index === 0 ? -1 : invite_index - 1;
}

/**
 * Merge an unread indicator update. The last read index only moves forward, and
 * the separator index is frozen the first time a conversation has unread
 * messages so the "New Messages" line does not drift while reading.
 *
 * Only a `seed_separator` update may place the line, anchored at the read
 * position it carries (`-1` when never-read). Ordinary read updates must never
 * place it: they run as the user reads, so they would anchor the line at an
 * already read message.
 *
 * `force_unread` rewinds last-read and repositions the separator (Mark as unread).
 *
 * `unread_count` is the live count, while `unread_since_last_visit` keeps how
 * many messages arrived after the last visit even once they have been read.
 *
 * @param {{last_read_message_index?: number|null, unread_count?: number, separator_after_index?: number|null, unread_since_last_visit?: number}|null|undefined} current
 * @param {{last_read_message_index?: number|null, unread_count?: number|null, seed_separator?: boolean, force_unread?: boolean}} next
 * @returns {{last_read_message_index: number|null, unread_count: number, separator_after_index: number|null, unread_since_last_visit: number}}
 */
export function mergeUnreadIndicator(current, next = {}) {
    const merged = {
        last_read_message_index: current?.last_read_message_index ?? null,
        unread_count: current?.unread_count ?? 0,
        separator_after_index: current?.separator_after_index ?? null,
        unread_since_last_visit: current?.unread_since_last_visit ?? 0,
    };

    // Explicit rewind from "Mark as unread": last-read may move backward and
    // the separator is placed at the new anchor (use -1 when nothing is read).
    if (next.force_unread) {
        const anchor =
            next.last_read_message_index == null
                ? -1
                : next.last_read_message_index;

        merged.last_read_message_index =
            anchor < 0 ? null : next.last_read_message_index;
        merged.separator_after_index = anchor;

        if (next.unread_count != null) {
            merged.unread_count = Math.max(0, next.unread_count);
            merged.unread_since_last_visit = merged.unread_count;
        }

        return merged;
    }

    if (next.last_read_message_index != null) {
        merged.last_read_message_index =
            merged.last_read_message_index == null
                ? next.last_read_message_index
                : Math.max(
                      merged.last_read_message_index,
                      next.last_read_message_index,
                  );
    }

    if (next.unread_count != null) {
        merged.unread_count = Math.max(0, next.unread_count);
    }

    // Anchored at the read position of this update, not the merged one, which
    // may already have moved on. Never-read (null last-read) with unread uses
    // -1 so every message from index 0 is below the New Messages line.
    if (
        next.seed_separator &&
        merged.separator_after_index == null &&
        merged.unread_count > 0
    ) {
        merged.separator_after_index =
            next.last_read_message_index == null
                ? -1
                : next.last_read_message_index;
    }

    if (merged.separator_after_index != null) {
        merged.unread_since_last_visit = Math.max(
            merged.unread_since_last_visit,
            merged.unread_count,
        );
    }

    return merged;
}
